<template>
  <div>
    <div v-if="strategyDetails.final_result">
      <div class="d-flex justify-content-between">
        <h3 class="text-primary">
          {{ title }}
        </h3>
        <b-dropdown no-caret type="link" variant="link" class="mb-2" dropleft>
          <template #button-content>
            <span class="monospace text-secondary">...</span>
          </template>
          <b-dropdown-item @click="$bvModal.show('tech-details')"><span class="material-symbols-outlined filled p-1" style="vertical-align: sub;">smart_toy</span> Technical info</b-dropdown-item>
        </b-dropdown>
      </div>
      <p>
        Period: {{ formatDate(strategyDetails.request_payload.from, true) }} -
        {{ formatDate(strategyDetails.request_payload.to, true) }}
      </p>

      <b-alert variant="primary" class="d-flex align-items-center" show v-if="strategyDetails.request_payload.status !== 'COMPLETED'">
        <div class="blob-inline mr-2">
          <span></span>
          <span></span>
        </div>
        Early insights loaded, more on the way
      </b-alert>

      <template v-if="screenshots.length">
        <div style="display: flex; flex-wrap: wrap; gap: 10px;">
          <div v-for="(step, i) in screenshots" :key="i" class="position-relative" style="border: 1px solid var(--exatom-grey); max-height: 150px; overflow: hidden; cursor: pointer;"
               @click="openModal(step.uri, step.name)"
               @mouseenter="showScreenshotActions($event, `screenshotAction-${step.uri}`)"
               @mouseleave="hideScreenshotActions($event, `screenshotAction-${step.uri}`)"
          >
            <img :src="step.uri"
                 :alt="step.name"
                 width="150"
                 style="padding: 2px;"
            />
            <div class="position-absolute btn btn-xs btn-light d-none" style="right: 8px; bottom: 8px; cursor: pointer;"
                 :ref="`screenshotAction-${step.uri}`"
                 @click.stop.prevent="downloadUri(step.uri, screenshotFileName(step.name))">
              <span class="material-symbols-outlined" style="font-size: 16px; vertical-align: middle; cursor: pointer;">download</span>
            </div>
          </div>
        </div>
      </template>

      <div contenteditable="true" class="mt-3" style="word-break: break-word;">
        <template v-if="summary">
          <h4>Summary</h4>
          <p v-if="typeof summary === 'string'">{{ summary }}</p>
          <template v-if="isPlainObject(summary) && summary['content-type']">
            <template v-if="summary['content-type'] === 'text/html'">
              <div v-html="summary.data"></div>
            </template>
          </template>
        </template>
        <div v-for="([key, value], index) in editableContent" :key="index" class="mb-3">
          <template v-if="Array.isArray(value)">
            <h4>{{ key.replaceAll('_', ' ') | capitalizeWords }}</h4>
            <ul>
              <li class="mb-2" v-for="(item, i) in value" :key="i">
                <template v-if="typeof item === 'string'">
                  {{ item }}
                </template>
                <template v-else-if="isPlainObject(item)">
                  <template v-if="item['content-type'] === 'text/html'">
                    <h6 v-if="item.name">{{ item.name }}</h6>
                    <span v-html="item.data"></span>
                  </template>
                  <template v-else>
                    {{ item }}
                  </template>
                </template>
              </li>
            </ul>
          </template>
          <template v-else-if="isPlainObject(value)">
            <h4>{{ key.replaceAll('_', ' ') | capitalizeWords }}</h4>
            <ul>
              <div class="mb-2" v-for="([subKey, subValue], i) in Object.entries(value)" :key="i">
                <li>
                  <template v-if="typeof subValue === 'string'">
                    {{ subValue }}
                  </template>
                  <template v-else-if="isPlainObject(subValue)">
                    <template v-if="subValue['content-type'] === 'text/html'">
                      <b>{{ subKey | capitalizeWords }}</b>
                      <br/>
                      <span v-html="subValue.data"></span>
                    </template>
                    <template v-else>
                      <b>{{ subKey | capitalizeWords }}</b>: {{ subValue }}
                    </template>
                  </template>
                </li>
              </div>
            </ul>
          </template>
          <template v-else-if="typeof value === 'string'">
            <h4>{{ key.replaceAll('_', ' ') | capitalizeWords }}</h4>
            {{ value }}
          </template>
        </div>
      </div>

      <div class="suggestions-panel mt-4" v-if="hasSuggestions || loadingSuggestions">
        <div class="d-flex justify-content-between align-items-center">
          <h4>Eureka Suggestions <span class="text-gradient-blue-green" style="vertical-align: super; font-size: 0.75rem;">AI</span></h4>
          <b-spinner small v-if="loadingSuggestions"></b-spinner>
        </div>

        <div v-if="hasSuggestions" class="suggestions-container">
          <button
            v-for="(suggestion, index) in suggestions"
            :key="index"
            class="action-button"
            :class="{ 'action-button-special': suggestion.action !== false }"
            @click="handleSuggestionClick(suggestion)"
          >
            {{ suggestion.label || `Suggestion ${index + 1}` }}
          </button>
        </div>
        <div v-else class="text-muted">
          Fetching suggestions...
        </div>
      </div>
    </div>
    <div v-else>
      <b-alert show variant="primary" class="mb-0 d-flex align-items-center" v-if="Array.isArray(strategyDetails.step_results) && !strategyDetails.step_results.length">
        <div class="blob-inline mr-2">
          <span></span>
          <span></span>
        </div>
        Getting things started, insights on their way
      </b-alert>
      <span v-else>Loading analysis details...</span>
    </div>

    <b-modal id="screenshot-modal"
             size="xl"
             hide-footer
             centered
             content-class="position-relative"
             @hide="closeModal"
             :title="selectedScreenshot.name">
      <template #modal-header="{ close }">
        <div class="row w-100 mr-5 d-flex align-items-center justify-content-between">
          <h5 class="modal-title">{{ selectedScreenshot.name }} {{ selectedScreenshot.indexName }}</h5>
          <div class="btn btn-xs btn-primary" @click="downloadUri(selectedScreenshot.uri, screenshotFileName(selectedScreenshot.name))">
            Download <span class="material-symbols-outlined" style="font-size: 16px; vertical-align: middle;">download</span>
          </div>
        </div>
        <button type="button" aria-label="Close" @click="close" class="close">×</button>
      </template>
      <template #default>
        <div class="position-absolute" style="left: 1rem; top: 50%;" @click="selectPreviousScreenshot()">
          <span class="material-symbols-outlined" style="font-size: 48px;" title="Previous screenshot">keyboard_arrow_left</span>
        </div>
        <img :src="selectedScreenshot.uri" :alt="selectedScreenshot.name" class="img-fluid">
        <div class="position-absolute" style="right: 1rem; top: 50%;" @click="selectNextScreenshot()">
          <span class="material-symbols-outlined" style="font-size: 48px;" title="Next screenshot">keyboard_arrow_right</span>
        </div>
      </template>
    </b-modal>

    <b-modal id="tech-details"
             size="lg"
             title="Technical analysis details"
             hide-footer>
      <b-card no-body>
        <b-tabs pills card>
          <b-tab title="Analysis" active>
            <b-card-text>
              <h5>Request Payload</h5>
              <pre>{{ strategyDetails.request_payload }}</pre>
              <h5>Step Results</h5>
              <pre v-for="(step, i) in strategyDetails.step_results" :key="i" style="white-space: break-spaces;">
                  {{ step }}
                </pre>
            </b-card-text>
          </b-tab>
          <b-tab title="Prompt" v-if="$root.hasRole('admin.internal') && strategyDetails && strategyDetails.final_result">
            <b-card-text>
              <h5>{{ strategyDetails.final_result.name }}</h5>
              <code style="white-space: break-spaces;">{{ prompt }}</code>
            </b-card-text>
          </b-tab>
        </b-tabs>
      </b-card>
    </b-modal>
  </div>
</template>

<style scoped>
[contenteditable="true"] {
  border: none !important;
  outline: none !important;
}
</style>

<script>
import { DateTime } from 'luxon';
import { downloadUri, isPlainObject } from "@/utils";

export default {
  name: 'AnalysisDetails',
  props: {
    title: {
      type: String,
      required: true
    },
    strategyDetails: {
      type: Object,
      required: true
    },
    strategyPrompt: {
      type: String,
      required: false
    },
    suggestions: {
      type: Array,
      default: () => []
    },
    loadingSuggestions: {
      type: Boolean,
      default: false
    },
    onSuggestionClick: {
      type: Function,
      default: null
    }
  },
  data() {
    return {
      selectedScreenshot: {}
    };
  },
  computed: {
    summary() {
      return this.strategyDetails?.final_result?.text?.summary || '';
    },
    screenshots() {
      if (!this.strategyDetails?.step_results?.length) return [];
      return this.strategyDetails.step_results.filter(s => s.uri && (s.visible === true || typeof s.visible === 'undefined'));
    },
    editableContent() {
      let textObj = this.strategyDetails.final_result?.text || {};
      if (typeof textObj === 'string') {
        return {}
        // We could show the partial results, but there is no reason to
        // textObj = {
        //   "partial results": [{
        //     "content-type": "text/html",
        //     "data": textObj.replace(/\n/g, '<br>'),
        //   }]
        // };
      }
      return Object.entries(textObj).filter(([key]) => key !== 'summary');
    },
    prompt() {
      return this.strategyPrompt.replaceAll(/<br\s*\/?>/g, "")
    },
    hasSuggestions() {
      return Array.isArray(this.suggestions) && this.suggestions.length > 0;
    }
  },
  mounted() {
    window.addEventListener('keyup', this.screenshotKeyUpHandler);
    window.addEventListener('keydown', this.screenshotKeyDownHandler);
  },
  beforeDestroy() {
    window.removeEventListener('keyup', this.screenshotKeyUpHandler);
    window.removeEventListener('keydown', this.screenshotKeyDownHandler);
  },
  methods: {
    downloadUri,
    isPlainObject,
    openModal(uri, name) {
      this.selectedScreenshot = {
        uri,
        name,
        index: this.screenshots.findIndex(s => s.uri === uri),
        indexName: `(${this.screenshots.findIndex(s => s.uri === uri) + 1}/${this.screenshots.length})`
      };
      this.$bvModal.show('screenshot-modal');
    },
    closeModal() {
      this.selectedScreenshot = {};
    },
    screenshotKeyUpHandler(event) {
      if (event.key === 'ArrowLeft') {
        this.selectPreviousScreenshot();
      }
    },
    screenshotKeyDownHandler(event) {
      if (event.key === 'ArrowRight') {
        this.selectNextScreenshot();
      }
    },
    selectNextScreenshot() {
      if (!this.selectedScreenshot?.uri) return;
      const idx = this.screenshots.findIndex(s => s.uri === this.selectedScreenshot.uri);
      let nextIdx = idx + 1;
      if (idx >= this.screenshots.length - 1) nextIdx = 0;
      this.selectedScreenshot = {
        ...this.screenshots[nextIdx],
        index: nextIdx,
        indexName: `(${nextIdx + 1}/${this.screenshots.length})`
      };
    },
    selectPreviousScreenshot() {
      if (!this.selectedScreenshot?.uri) return;
      const idx = this.screenshots.findIndex(s => s.uri === this.selectedScreenshot.uri);
      let prevIdx = idx - 1;
      if (idx <= 0) prevIdx = this.screenshots.length - 1;
      this.selectedScreenshot = {
        ...this.screenshots[prevIdx],
        index: prevIdx,
        indexName: `(${prevIdx + 1}/${this.screenshots.length})`
      };
    },
    showScreenshotActions(evt, ref) {
      if (evt.target.parentElement !== evt.fromElement && evt.target.parentElement.contains(evt.fromElement)) return;
      this.$refs[ref][0].classList.remove('d-none');
    },
    hideScreenshotActions(evt, ref) {
      if (evt.target.parentElement !== evt.toElement && evt.target.parentElement.contains(evt.toElement)) return;
      this.$refs[ref][0].classList.add('d-none');
    },
    screenshotFileName(name) {
      return `${this.$root.client.name} - ${name}.png`;
    },
    formatDate(dateStr, compact = false) {
      const dt = DateTime.fromISO(dateStr);
      return compact ? dt.toFormat('dd/LL/yy') : dt.toFormat('dd/LL/yyyy HH:mm');
    },
    handleSuggestionClick(suggestion) {
      if (typeof this.onSuggestionClick === 'function') {
        this.onSuggestionClick(suggestion);
      }
    }
  }
};
</script>

<style scoped>
.card {
  border: 1px solid #ccc;
  padding: 16px;
  border-radius: 8px;
  margin-bottom: 16px;
}
.collapse-transition-enter-active,
.collapse-transition-leave-active {
  transition: all 0.3s ease;
}
.collapse-transition-enter,
.collapse-transition-leave-to {
  max-height: 0;
  opacity: 0;
  overflow: hidden;
}
.nav-tabs .nav-link {
  cursor: pointer;
}
.suggestions-container {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  justify-content: flex-start;
}
.action-button {
  background: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 16px;
  padding: 4px 12px;
  font-size: 12px;
  color: #495057;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
  max-width: 425px;
  overflow: hidden;
  text-overflow: ellipsis;
}
.action-button:hover {
  border-color: #adb5bd;
}
.action-button-special {
  background-image: linear-gradient(to top left, #e4fcff, #defafd, #d9f9fc, #d3f7fa, #cef3fc, #cbeffe, #cbeaff, #cce5ff);
  font-weight: bold;
}
.action-button-special:hover {
  border-color: #007dfd;
}
</style>
